import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ListsView from '../components/screens/ListsView';
import useFamilyLists from '../hooks/useFamilyLists'; // kept only for family membership status
import { useLists } from '../hooks/useLists';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../lib/dateTime';
import {
  buildRecurrencePayload,
  createRecurrenceForm,
} from '../lib/recurrence';

const initialItemDraft = {
  title: '',
  notes: '',
  dueDate: '',
  priority: 'medium',
  recurrence: createRecurrenceForm(),
};

const editorDefaultsByScope = {
  personal: {
    icon: 'list.bullet',
    color: '#60a5fa',
  },
  family: {
    icon: 'cart',
    color: '#0f766e',
  },
};

function normalizeScope(scope) {
  return scope === 'family' ? 'family' : 'personal';
}

function buildEditorForm(scope = 'personal', overrides = {}) {
  const resolvedScope = normalizeScope(scope);
  const defaults = editorDefaultsByScope[resolvedScope];

  return {
    listId: '',
    name: '',
    scope: resolvedScope,
    icon: defaults.icon,
    color: defaults.color,
    ...overrides,
  };
}

function toEditorForm(list, scope) {
  const resolvedScope = normalizeScope(scope || list?.scope);
  const defaults = editorDefaultsByScope[resolvedScope];

  return {
    listId: list?.id || list?._id || '',
    name: list?.name || '',
    scope: resolvedScope,
    icon: list?.icon || defaults.icon,
    color: list?.color || defaults.color,
  };
}

function buildListPayload(form) {
  return {
    name: form.name,
    type: 'custom',
    color: form.color,
    icon: form.icon,
    position: 1,
    scope: form.scope, // forwarded as scopeType by workspaceClient
  };
}

function buildItemPayload(draft) {
  return {
    title: draft.title,
    notes: draft.notes,
    dueDate: fromDateTimeLocalValue(draft.dueDate) || null,
    status: 'open',
    priority: draft.priority || 'medium',
    recurrence: buildRecurrencePayload(draft.recurrence),
  };
}

function CollectionsLoadingScreen() {
  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <div className="rounded-[24px] border border-app-border/70 bg-app-shell p-8 shadow-panel">
        <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-app-accent-strong">
          Loading Collections
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-app-ink">
          Preparing your lists
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-7 text-app-muted">
          Pulling personal and family collections together before the page becomes interactive.
        </p>
        <p className="mt-6 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800">
          Loading collections...
        </p>
      </div>
    </section>
  );
}

function ListsPage({ auth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { scope: routeScope, listId: routeListId } = useParams();
  const [mode, setMode] = useState('list');
  const [activeScope, setActiveScope] = useState('personal');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('open');
  const [editingListItemId, setEditingListItemId] = useState('');
  const [listItemDraft, setListItemDraft] = useState(initialItemDraft);
  const [listEditorForm, setListEditorForm] = useState(() => buildEditorForm());

  // Unified hook — the new backend endpoint returns personal + family lists together
  const listsState = useLists(auth.currentUser);
  // Kept only for family membership status (drives the scope toggle in the editor)
  const familyMeta = useFamilyLists(auth.currentUser);
  const family = familyMeta.family;

  const allLists = useMemo(() => listsState.lists, [listsState.lists]);

  const filteredLists = useMemo(() => {
    if (scopeFilter === 'personal') return allLists.filter((l) => l.scope !== 'family');
    if (scopeFilter === 'family') return allLists.filter((l) => l.scope === 'family');
    return allLists;
  }, [allLists, scopeFilter]);

  const activeList = listsState.selectedList;
  const activeListItems = listsState.activeListItems;
  const openList = listsState.openList;

  const handleOpenList = async (scope, listId) => {
    const nextScope = normalizeScope(scope);
    setActiveScope(nextScope);
    setMode('detail');
    setEditingListItemId('');
    setListItemDraft(initialItemDraft);
    await openList(listId, activeFilter, nextScope);
    navigate(`/lists/${nextScope}/${listId}`);
  };

  const handleListFilterChange = async (fieldOrValue, maybeValue) => {
    if (maybeValue === undefined) {
      const nextFilter = fieldOrValue;
      setActiveFilter(nextFilter);

      if (routeListId) {
        await openList(routeListId, nextFilter);
      }

      return;
    }

    const field = fieldOrValue;
    const value = maybeValue;

    if (field === 'filter') {
      setActiveFilter(value);
    }
  };

  const handleOpenListEditor = () => {
    if (mode === 'detail' && activeList) {
      setListEditorForm(toEditorForm(activeList, activeScope));
    } else {
      setListEditorForm(buildEditorForm(activeScope));
    }

    setMode('editor');
  };

  const handleSaveList = async () => {
    const payload = buildListPayload(listEditorForm);
    let savedList = null;

    if (listEditorForm.listId) {
      savedList = await listsState.updateList(payload);
    } else {
      savedList = await listsState.createList(payload);
    }

    if (!savedList?.id) {
      return;
    }

    setMode('detail');
    setListEditorForm(buildEditorForm());
    setActiveScope(listEditorForm.scope);
    setListItemDraft(initialItemDraft);
    await handleOpenList(listEditorForm.scope, savedList.id);
  };

  const handleAddListItem = async (event) => {
    event.preventDefault();

    if (!activeList?.id || !listItemDraft.title.trim()) {
      return;
    }

    const existingItem = activeListItems.find((item) => item.id === editingListItemId) || null;
    const payload = editingListItemId
      ? {
          title: listItemDraft.title,
          notes: listItemDraft.notes,
          dueDate: fromDateTimeLocalValue(listItemDraft.dueDate) || null,
          status: existingItem?.status || 'open',
          priority: listItemDraft.priority || existingItem?.priority || 'medium',
          recurrence: buildRecurrencePayload(listItemDraft.recurrence),
        }
      : buildItemPayload(listItemDraft);
    const savedItem = editingListItemId
      ? await listsState.updateListItem(editingListItemId, payload)
      : await listsState.createListItem(payload);

    if (!savedItem?.id) {
      return;
    }

    setEditingListItemId('');
    setListItemDraft(initialItemDraft);
  };

  const handleToggleListItemStatus = async (item) => {
    if (!activeList?.id || !item?.id) {
      return;
    }

    const nextStatus = item.status === 'completed' ? 'open' : 'completed';
    await listsState.updateListItem(item.id, { status: nextStatus });
  };

  const handleArchiveList = async () => {
    if (!activeList?.id) {
      return;
    }

    await listsState.updateList({ status: 'archived' });
    setMode('list');
    setEditingListItemId('');
    setListItemDraft(initialItemDraft);
    navigate('/lists');
  };

  const handleDeleteList = async () => {
    if (!activeList?.id) {
      return;
    }

    const deleted = await listsState.deleteList();

    if (!deleted) {
      return;
    }

    setMode('list');
    setEditingListItemId('');
    setListItemDraft(initialItemDraft);
    navigate('/lists');
  };

  const handleEditListItem = (item) => {
    setEditingListItemId(item?.id || '');
    setListItemDraft({
      title: item?.title || '',
      notes: item?.notes || '',
      dueDate: toDateTimeLocalValue(item?.dueDate),
      priority: item?.priority || 'medium',
      recurrence: createRecurrenceForm(item?.recurrence),
    });
  };

  const handleDeleteListItem = async (itemId) => {
    const deleted = await listsState.deleteListItem(itemId);

    if (!deleted) {
      return;
    }

    if (editingListItemId === itemId) {
      setEditingListItemId('');
      setListItemDraft(initialItemDraft);
    }
  };

  useEffect(() => {
    if (!routeListId) {
      if (location.state?.mode === 'editor') {
        const requestedScope = normalizeScope(location.state?.scope);
        setActiveScope(requestedScope);
        setListEditorForm(buildEditorForm(requestedScope));
        setMode('editor');
      } else {
        setMode('list');
      }

      return;
    }

    const nextScope = normalizeScope(routeScope);
    setActiveScope(nextScope);
    setMode('detail');
  }, [location.state, routeListId, routeScope]);

  useEffect(() => {
    if (!routeListId) {
      return;
    }

    void openList(routeListId, activeFilter, normalizeScope(routeScope));
  }, [activeFilter, openList, routeListId, routeScope]);

  if (!listsState.hasLoadedLists) {
    return <CollectionsLoadingScreen />;
  }

  const listsStatus = {
    type: listsState.isLoading ? 'loading' : listsState.listError ? 'error' : 'success',
    message: listsState.listError
      ? listsState.listError
      : listsState.isLoading
        ? 'Loading collections...'
        : allLists.length
          ? `${allLists.length} collection${allLists.length === 1 ? '' : 's'} loaded.`
          : 'No collections yet.',
  };

  return (
    <ListsView
      currentUser={auth.currentUser}
      mode={mode}
      listsStatus={listsStatus}
      listEditorStatus={{ type: 'idle', message: '' }}
      family={family}
      allLists={filteredLists}
      scopeFilter={scopeFilter}
      onScopeFilterChange={setScopeFilter}
      activeScope={activeScope}
      activeList={activeList}
      activeListItems={activeListItems}
      activeFilter={activeFilter}
      editingListItemId={editingListItemId}
      listItemDraft={listItemDraft}
      listEditorForm={listEditorForm}
      onOpenList={handleOpenList}
      onOpenListEditor={handleOpenListEditor}
      onListFilterChange={handleListFilterChange}
      onListItemDraftChange={(field, value) =>
        setListItemDraft((current) => ({ ...current, [field]: value }))
      }
      onAddListItem={handleAddListItem}
      onToggleListItemStatus={handleToggleListItemStatus}
      onEditListItem={handleEditListItem}
      onDeleteListItem={handleDeleteListItem}
      onCancelListItemEdit={() => {
        setEditingListItemId('');
        setListItemDraft(initialItemDraft);
      }}
      onEditorChange={(field, value) =>
        setListEditorForm((current) => {
          if (field !== 'scope') {
            return { ...current, [field]: value };
          }

          const currentScope = normalizeScope(current.scope);
          const nextScope = normalizeScope(value);
          const currentDefaults = editorDefaultsByScope[currentScope];
          const nextDefaults = editorDefaultsByScope[nextScope];

          return {
            ...current,
            scope: nextScope,
            icon: !current.icon || current.icon === currentDefaults.icon ? nextDefaults.icon : current.icon,
            color:
              !current.color || current.color === currentDefaults.color
                ? nextDefaults.color
                : current.color,
          };
        })
      }
      onSaveList={handleSaveList}
      onArchiveList={handleArchiveList}
      onDeleteList={handleDeleteList}
      onBackToCollections={() => {
        setMode('list');
        setEditingListItemId('');
        setListItemDraft(initialItemDraft);
        navigate('/lists');
      }}
    />
  );
}

export default ListsPage;
